import type { ReactNode } from "react";
import {
  ContextMenu,
  ContextMenuCheckboxItem,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuSeparator,
  ContextMenuSub,
  ContextMenuSubContent,
  ContextMenuSubTrigger,
  ContextMenuTrigger,
} from "@/shared/components/ui/context-menu";
import { RAID_ROLE_ICONS } from "@/shared/lib/roleIcons";
import { RAID_ROLE_LABELS, RAID_ROLES } from "@/shared/lib/roles";
import {
  classColorStyle,
  WOW_CLASS_LABELS,
  WOW_CLASSES,
} from "@/shared/lib/wowClass";
import type { PlayerChange } from "../setup.overrides";
import type { SetupPlayer } from "../setup.types";

// Right-click menu shared by members and stand-ins: every edit is a
// PlayerChange; only the tail items differ by kind (reset vs edit/delete).
export function PlayerContextMenu({
  player,
  onChange,
  onEditStandIn,
  onDeleteStandIn,
  leadingItems,
  additionalItems,
  children,
}: {
  player: SetupPlayer;
  onChange: (change: PlayerChange) => void;
  onEditStandIn: (player: SetupPlayer) => void;
  onDeleteStandIn: (id: string) => void;
  leadingItems?: ReactNode;
  additionalItems?: ReactNode;
  children: ReactNode;
}) {
  const hasOverride =
    player.overrideWowClass != null || player.overrideRaidRole != null;

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{children}</ContextMenuTrigger>
      <ContextMenuContent>
        {leadingItems}
        <ContextMenuSub>
          <ContextMenuSubTrigger>Byt klass</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {WOW_CLASSES.map((c) => (
              <ContextMenuCheckboxItem
                key={c}
                checked={player.wowClass === c}
                style={classColorStyle(c)}
                onSelect={() => onChange({ wowClass: c })}
              >
                {WOW_CLASS_LABELS[c]}
              </ContextMenuCheckboxItem>
            ))}
          </ContextMenuSubContent>
        </ContextMenuSub>

        <ContextMenuSub>
          <ContextMenuSubTrigger>Byt roll</ContextMenuSubTrigger>
          <ContextMenuSubContent>
            {RAID_ROLES.map((r) => {
              const Icon = RAID_ROLE_ICONS[r];
              return (
                <ContextMenuCheckboxItem
                  key={r}
                  checked={player.raidRole === r}
                  onSelect={() => onChange({ raidRole: r })}
                >
                  <Icon />
                  {RAID_ROLE_LABELS[r]}
                </ContextMenuCheckboxItem>
              );
            })}
          </ContextMenuSubContent>
        </ContextMenuSub>

        <ContextMenuSeparator />
        <ContextMenuItem
          onSelect={() => onChange({ benched: !player.benched })}
        >
          {player.benched ? "Ta in i setupen" : "Bänka"}
        </ContextMenuItem>

        {hasOverride && (
          <ContextMenuItem
            onSelect={() => onChange({ wowClass: null, raidRole: null })}
          >
            Återställ ändringar
          </ContextMenuItem>
        )}

        {player.kind === "stand_in" && (
          <>
            <ContextMenuItem onSelect={() => onEditStandIn(player)}>
              Redigera
            </ContextMenuItem>
            <ContextMenuSeparator />
            <ContextMenuItem
              variant="destructive"
              onSelect={() => onDeleteStandIn(player.id)}
            >
              Ta bort
            </ContextMenuItem>
          </>
        )}

        {additionalItems}
      </ContextMenuContent>
    </ContextMenu>
  );
}
